/// <reference types="node" />
/// <reference types="barcode" />

/**
 * A class for showing webcamera stream on the webpage and parsing the middle 1/3 of the video
 * for barcode parsing.
 *
 * The constructor will recieve a configuration object, please see BarcodeScannerConfig for
 * documentation. All properties of the configuration has default values, so you can
 * give an empty object if you like.
 *
 * After constructing the object, you need to start the video stream
 */
class BarcodeScanner {

    /**
     * The video element
     */
    private readonly element:HTMLVideoElement;

    private config:BarcodeScannerConfig;

    private readonly debug:boolean;

    /** The track of the video stream, set in start */
    private track:MediaStreamTrack;

    /** Error messages are stored in this array */
    private readonly errors:string[];

    /**
     * The options used for getUserMedia().
     * Changes must be done before calling start()
     */
    public userMediaOptions:MediaStreamConstraints = {
        audio: false,
        video: {
            facingMode: "environment",
            width: {
                min: 640,
                ideal: 2048,
                max: 3200
            },
            height: {
                min: 480,
                ideal: 1536,
                max: 2400
            },
            advanced: [
                { zoom: 1 } // we want to control the zoom some times
            ]
        }
    }

    // /**
    //  * Contains the cropped image used for barcde parsing.
    //  * Contains image only after function captureImage() has run.
    //  */
    // public image:ImageBitmap;

    /**
     *
     * @param config The configuration
     */
    constructor(config:BarcodeScannerConfig) {
        this.initConsoleCapture(); // Init capturing all data that is sent to console

        // The default configuration
        let defaultConfig:BarcodeScannerConfig = {
            debug: false,
            id: "video",
            errorCallback: function() {},   // empty function
            okCallback: function() {},      // empty function
            logCallback: function() {},     // empty function, but could be a function
            userOutputSelector: "",         // Empty string means no output to user
            barcodeDetectorErrorMessage: "The barcode detection functionality is not available on your device",
            mediaDevicesErrorMessage: "The functionality to connect to webcamera is not available on your device",
            webcameraMissingErrorMessage: "A webcamera is not available on your device",
            httpErrorMessage: "You are visiting on HTTP, please switch to HTTPS to enable the webcamera"
        };

        // Merge the default config object with given config object
        this.config = {...defaultConfig, ...config};

        // https://stackoverflow.com/a/13671174
        this.element = <HTMLVideoElement>document.getElementById(this.config.id);
        this.debug = this.config.debug;
        this.errors = [];

        // Set up the support object with error messages
        const msgs:BarcodeSupportErrorMessages = {
            barcodeDetectorErrorMessage: this.config.barcodeDetectorErrorMessage,
            httpErrorMessage: this.config.httpErrorMessage,
            mediaDevicesErrorMessage: this.config.mediaDevicesErrorMessage,
            webcameraMissingErrorMessage: this.config.webcameraMissingErrorMessage,
            samsungInternetBrowserSupportErrorMessage: "Du benytter webleseren Samsung Internett, vi ber deg bytte til Chrome for bedre støtte for skanning."
        };
        const support = new BarcodeSupport(msgs);
        const isSupported = support.hasSupport();

        // If no support found, then output error messages
        if (!isSupported) {
            const msgs = support.getErrorMessages();
            for (let i=0; i<msgs.length; i++) {
                this.errors.push(msgs[i]);
            }
            const msg = support.getErrorMessagesAsString();
            this.out(msg, true);
        }

        // Commented away until we are sure the new support function works.
        // // @ts-ignore Ignore IDE error for BarcodeDetector not found
        // if (typeof BarcodeDetector === "undefined") {
        //     this.errors.push(this.config.barcodeDetectorErrorMessage);
        //     this.out(this.config.barcodeDetectorErrorMessage, true, true);
        // }
        // if (navigator.mediaDevices===undefined) {
        //     let msg = this.config.mediaDevicesErrorMessage;
        //     this.errors.push(this.config.mediaDevicesErrorMessage);
        //     if (window.location.protocol!=="https:") {
        //         msg += ". "+this.config.httpErrorMessage;
        //         this.errors.push(this.config.httpErrorMessage);
        //     }
        //     this.out(msg, true,true);
        // }
        // if (!navigator.mediaDevices.getUserMedia) {
        //     this.errors.push(this.config.webcameraMissingErrorMessage);
        //     this.out(this.config.webcameraMissingErrorMessage, true,true);
        // }
    }

    /**
     * Initialize capture of the javacript console
     * @private
     */
    private initConsoleCapture() {
        // Define an array logs
        console.logs = [];
        console.errors = [];
        console.stdlog = console.log.bind(console);
        console.stderror = console.error.bind(console);

        console.log = function() {
            // @ts-ignore Ignore the error for apply
            console.stdlog.apply(console, arguments);
            // @ts-ignore Ignore the error in push' argument
            console.logs.push(Array.from(arguments));
        }

        console.error = function() {
            // @ts-ignore
            console.stderror.apply(console, arguments);
            // @ts-ignore
            console.logs.push(Array.from(arguments));
        }
    }

    /**
     * This will output text to the user, if the configuration for the class was given
     * a valid userOutputSelector.
     * @param text The text to be outputted. This string will be added in a paragraph.
     * @private
     */
    private outputUserFeedback(text:string) {
        if (this.config.userOutputSelector!=="") {
            //$(this.config.userOutputSelector).html("<p>"+text+"</p>");
            let content = $(this.config.userOutputSelector).html();
            content += "<p>"+text+"</p>";
            $(this.config.userOutputSelector).html(content);
        }
    }

    /**
     * Get the track for the webcamera stream.
     * The track is defined after start() has been run. Before this, there is no
     * track to return.
     */
    public getTrack():MediaStreamTrack {
        return this.track;
    }


    // private getMaxResulution() {
    //     let options = {
    //         audio: false,
    //         video: {
    //             facingMode: "environment",
    //             width: { max: 4096 },
    //             height: { max: 2160 },
    //         }
    //     };
    //     let self = this;
    //     navigator.mediaDevices.getUserMedia(options)
    //         .then(function (stream) {
    //             let tracks = stream.getVideoTracks();
    //             let track = tracks[0];
    //
    //             // Get the max resolution for the webcamera
    //             let maxResolution = {
    //                 width: track.getCapabilities().width.max,
    //                 height: track.getCapabilities().height.max
    //             };
    //         });
    // }


    /**
     * Start the video stream
     * @param {Function} successCallback A function to call when starting the webcamera has been successfull
     */
    public start(successCallback?:Function) {
        this.out("Start webcamera");
        let self = this;
        let video:HTMLVideoElement = this.element;
        this.out("Set up video stream");

        // try {
        //     const panTiltZoomPermissionStatus = await navigator.permissions.query({
        //         name: "camera",
        //         panTiltZoom: true
        //     });
        //
        //     if (panTiltZoomPermissionStatus.state == "granted") {
        //         console.log("granted!");
        //     }
        //
        //     panTiltZoomPermissionStatus.addEventListener("change", function() {
        //         console.log("Changed permissions");
        //     });
        // }
        // catch (error) {
        //     console.log("error");
        //     console.log(error);
        // }


        // Documentation for getting webcamera stream:
        // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
        // We are setting up camera without audio, and we are asking for environment camera, that means
        // not selfie camera for phones, but facing away from person. We are interested in maximum resolution
        // to have the best readings of barcodes, so we set max to a high value, so that the know we get max from
        // the camera.
        navigator.mediaDevices.getUserMedia(this.userMediaOptions)
            .then(function (stream) {
                self.out("Starting to get video streams");
                video.srcObject = stream;

                let tracks = stream.getVideoTracks();
                self.out("Number of videotracks: "+tracks.length);
                self.track = tracks[0];

                // About advanced constrains, read these links:
                let settings = self.track.getSettings();
                //self.out("zoom level: "+settings.zoom);
                // https://web.dev/camera-pan-tilt-zoom/
                // https://developer.mozilla.org/en-US/docs/Web/API/Media_Streams_API/Constraints
                // supports
                // const supports = navigator.mediaDevices.getSupportedConstraints();
                // console.log(supports);

                // If attribute "autoplay" is not present in the video tag, then this
                // makes sure the video image is shown:
                video.play();
                self.out("Video track play() run");

                let output = "Video width="+settings.width+" height="+settings.height;
                self.out(output);

                // If success callback function is given, then run it
                if (successCallback !== undefined) {
                    // TODO try catching errors from callback, so that these are not caught by catch below
                    successCallback();
                }
            })
            .catch(function (error) {
                self.out("Ikke mulig å hente videostrøm, feilmelding:", true, true);
                self.out(error, true, true);
            })
            // When initializing the web camera is finished, then send logs to server for debugging
            .finally(function() {
                let list = self.getConsoleLog();
                let string = "Collection of logging from console:\n"+list.join("\n");
                self.config.logCallback(string);
                let errors = "Collection of error messages in console:\n"+self.getErrorMessages().join("\n");
                self.config.logCallback(errors);
            });
    }

    /**
     * Stop the video stream
     */
    public stop() {
        this.out("Stop the video stream");
        this.track.stop();
    }

    /**
     * Output text to console (only if debug mode is on)
     * @param text  The text to output to console
     * @param error mark output as error? Default is false.
     * @param force Force output if debug mode is on
     * @private
     */
    private out(text:string, error:boolean=false, force:boolean=false) {
        if (this.debug || force) {
            if (error) { console.error(text) }
            else { console.log(text) }
        }

        // if log callback function is given, then send log string to it
        if (this.config.logCallback!==undefined) {
            this.config.logCallback(text);
        }
    }

    /**
     * Capture a picture from video stream.
     *
     * This function will only parse the first 1/3 of the top of the image
     * for barcodes.
     */
    public captureImage() {
        this.out("Start capturing image to parse barcodes", false, false);
        let self = this;

        // debugging the chrome android on remote computer:
        // https://developer.chrome.com/docs/devtools/remote-debugging/
        let imageCapture = new ImageCapture(this.track);
        imageCapture.grabFrame()
            .then(imageBitmap => {

                // Crop the image, always use the middle 1/3rd of the image to parse barcode.
                // This is the image showing the part to parse:
                //  0        x        n
                //   ┌----------------┐
                //   |                |
                //   |----------------|
                // y |  This part!    |
                //   |----------------|
                //   |                |
                // n └----------------┘
                //
                let selectedHeight = imageBitmap.height/3;
                let sx = 0;
                let sy = Math.floor(selectedHeight); // 1/3 height as integer
                let sh = sy;
                let sw = imageBitmap.width;

                // Genrerate output
                const sourceBitmapInfo = "Video X="+imageBitmap.width+" y="+imageBitmap.height;
                let output = sourceBitmapInfo+" | Cropped x="+sw+" y="+sh;
                self.out(output);

                self.parseBarcode(imageBitmap);

                // Found that cropping ruined the scanning on some devices. Removed cropping for some time.
                // We need to debug the cropping function by sending the image data to backoffice and store the images
                // to disk to see that they are cropped correctly. Issue #1566
                // createImageBitmap(imageBitmap, sx, sy, sw, sh)
                //      .then(function(image) {
                //          $.ajax({
                //              method: "post",
                //              url: "index.php?r=jobb/ajaxTempCroppedImage",
                //              data: image
                //          })
                //      });
                //self.out("ferdig med bitmap");
            });
    }


    /**
     * Do the parsing of barcode/QR code
     * @param image
     * @private
     */
    private parseBarcode(image) {
        const self = this;

        // About barcode scanning and formats:
        // https://developer.mozilla.org/en-US/docs/Web/API/Barcode_Detection_API
        // Formats not in use: "aztec", "data_matrix", "pdf417"
        let formats = [
            "code_128", "code_39", "code_39", "code_93", "codabar", "ean_13", "ean_8",
            "itf", "qr_code", "upc_a", "upc_e"
        ];

        // @ts-ignore
        BarcodeDetector.getSupportedFormats()
            .then(function (formats) {
                if (formats.length === 0) {
                    let msg = "Device does not support any barcodes types, getSupportedFormats() retrurned empty list.";
                    self.out(msg, true, true);
                    self.outputUserFeedback("Din enhet har ikke støtte for strekkoder");
                }
                else {
                    self.out("Formats supported: " + formats.join("|"), false);
                    // @ts-ignore Ignore error for class BarcodeDetector not found by IDE
                    const barcodeDetector = new BarcodeDetector({formats: formats});
                    barcodeDetector.detect(image)
                        .then(list => { // list of detectedBarcode
                            if (list.length === 0) {
                                self.config.errorCallback();
                                self.out("No barcodes found", false);
                            }
                            else {
                                let format = list[0].format;
                                let value = list[0].rawValue;
                                self.out("Found: " + value + " Format: " + format);
                                self.config.okCallback(value);
                            }
                        }, reject => {
                            self.config.errorCallback();
                            self.out("Barcode scanning returned rejected status:", true);
                            self.out(reject, true);
                            self.outputUserFeedback("Det oppstod en feil: " + reject); // Output rejection info to the user
                        })
                        .catch(e => {
                            self.config.errorCallback();
                            self.out("An error was detected: ", true);
                            self.outputUserFeedback("An error was detected: " + e.toString());
                        });
                }
            })
            .catch(err => {
                self.out("Error occurred: " + err, true, true);
            });
        }

    /**
     * Detect if there are error messages. The constructor will add error messages
     * if the users webbrowser does not support barcode scanning, or webcamera
     * streaming is not supported, or there is no webcamera available.
     */
    public hasErrors() {
        return this.errors.length>0;
    }

    /**
     * All error messages are detected on constructor and can be retrieved here.
     * The messages are predefined or translated in the configuration object given
     * to constructor, or else default english messages are used.
     */
    public getErrorMessages() {
        return this.errors;
    }

    /**
     * Get all error messages as a string.
     */
    public getErrorMessagesAsString() {
        let output = ""; // init
        if (this.hasErrors()) {
            let msgs = this.getErrorMessages();
            // if one error message:
            if (msgs.length==1) { output = msgs[0]; }
            // if multiple error messages:
            else {
                output = "<ul>";
                this.getErrorMessages().forEach(function (value, index) {
                    output += "<li>" + value + "</li>";
                });
                output += "</ul>";
            }
            // Add ekstra information about support for barcode detector API
            output += "<br>Din enhet må ha støtte for webkamera og strekkoder for å bruke denne "+
                "funksjonen. Dette er kun støttet av nyere Android-enheter foreløpig. "+
                "<a href=\"https://caniuse.com/mdn-api_barcodedetector\" target=\"_blank\">Se liste over støttede "+
                "enheter her</a>.";
        }
        return output;
    }

    /**
     * Get the console log
     */
    public getConsoleLog():string[] {
        return console.logs;
    }

    /**
     * Get the console errors
     */
    public getConsoleError():string[] {
        return console.errors;
    }

    // /**
    //  * Get a list of cameras to choose from
    //  */
    // public getCameras() {
    //     let self = this;
    //     navigator.mediaDevices.enumerateDevices().
    //         then(function(devices) {
    //             //console.log(devices);
    //             let output = {};
    //             for (let i = 0; i !== devices.length; ++i) {
    //
    //                 const deviceInfo = devices[i];
    //
    //                 //const option = document.createElement("option");
    //                 //option.value = deviceInfo.deviceId;
    //
    //                 if (deviceInfo.kind === "videoinput") {
    //                     //option.text = deviceInfo.label || "camera " + (videoSelect.length + 1);
    //                     //videoSelect.appendChild(option);
    //                     output[i] = {
    //                         id: deviceInfo.deviceId,
    //                         label: deviceInfo.label
    //                     }
    //                 }
    //                 else {
    //                     //console.log("Found another kind of device: ", deviceInfo);
    //                 }
    //             }
    //             return output;
    //         }).catch(function() {
    //             self.out("Error enumerating camera devices");
    //         });
    // }


}


/**
 * The configuration object to give to BarcodeScanner
 * https://stackoverflow.com/a/31042026
 */
interface BarcodeScannerConfig {
    debug?:boolean;
    id:string;
    userOutputSelector:string; // A selector for a HTML element to write feedback to user to
    errorCallback?:Function;
    okCallback?:Function;

    /**
     * An optional callback function for logging. This function will always be called
     * then logging inside the BarcodeScanner library.
     */
    logCallback?:Function;

    // error messages
    barcodeDetectorErrorMessage:string; // error if barcode API is not available
    mediaDevicesErrorMessage:string;    // error if media devices API is not available
    webcameraMissingErrorMessage:string;// error if webcamera is not available
    httpErrorMessage:string;            // error if user is not on secure http (https)
}

/**
 * Defining interface for object Console, which affects the "console".
 */
interface Console {
    stdlog: void,
    stderror: void,
    logs: string[],  // The data pushed to console.log will be stored in console.logs[]
    errors: string[] // The errors pushed to console
}